import { FC, useState, useEffect } from "react";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as ZapIcon } from "../../assets/zapIcon.svg";
import { ReactComponent as LayersIcon } from "../../assets/layersIcon.svg";
import { ReactComponent as BookIcon } from "../../assets/bookIcon.svg";
import { ReactComponent as GraphIcon } from "../../assets/graphIcon.svg";
import { ReactComponent as TelescopeIcon } from "../../assets/telescopeIcon.svg";
import { ReactComponent as GraduationHatIcon } from "../../assets/graduationHatIcon.svg";
import { defaultAssistantSettings as defaultConfig } from "../../common/utilities/constants/constants";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import { getLogoImg } from "../../common/utilities/logo/logo.util";
import { Theme } from "../../common/enums/theme.enum";
import { Logo } from "../../common/enums/logo.enum";

const QuestionBubble = ({
    icon,
    text,
    handleClick,
    className = ""
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    text: string;
    handleClick: (arg1: string) => void;
    className?: string;
}) => {
    return (
        <Button
            className={`question-bubble ${className}`}
            variant="outlined"
            onClick={() => handleClick(text)}
        >
            <SvgIcon className="question-bubble__icon" component={icon} inheritViewBox />
            <span className="question-bubble__text">{text}</span>
        </Button>
    );
};

export const ChatBackground = ({ handleQuestion }: { handleQuestion: (arg1: string) => void }) => {
    const { theme, logo, color } = ThemeContainer.useContainer();
    const { config } = FirebaseAuthContainer.useContainer();
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [sampleQuestion1, setSampleQuestion1] = useState<string>("");
    const [sampleQuestion2, setSampleQuestion2] = useState<string>("");
    const [sampleQuestion3, setSampleQuestion3] = useState<string>("");
    const [sampleQuestion4, setSampleQuestion4] = useState<string>("");
    const [sampleQuestion5, setSampleQuestion5] = useState<string>("");
    const [sampleQuestion6, setSampleQuestion6] = useState<string>("");

    useEffect(() => {
        if (!config) return;

        setTitle(config.greeting ?? defaultConfig.title);
        setDescription(config.description ?? defaultConfig.description);

        setSampleQuestion1(
            config.suggestedQuestions.length > 0
                ? config.suggestedQuestions[0].question
                : defaultConfig.sampleQuestions.length > 0
                  ? defaultConfig.sampleQuestions[0]
                  : ""
        );
        setSampleQuestion2(
            config.suggestedQuestions.length > 1
                ? config.suggestedQuestions[1].question
                : defaultConfig.sampleQuestions.length > 1
                  ? defaultConfig.sampleQuestions[1]
                  : ""
        );
        setSampleQuestion3(
            config.suggestedQuestions.length > 2
                ? config.suggestedQuestions[2].question
                : defaultConfig.sampleQuestions.length > 2
                  ? defaultConfig.sampleQuestions[2]
                  : ""
        );
        setSampleQuestion4(
            config.suggestedQuestions.length > 3
                ? config.suggestedQuestions[3].question
                : defaultConfig.sampleQuestions.length > 3
                  ? defaultConfig.sampleQuestions[3]
                  : ""
        );
        setSampleQuestion5(
            config.suggestedQuestions.length > 4
                ? config.suggestedQuestions[4].question
                : defaultConfig.sampleQuestions.length > 4
                  ? defaultConfig.sampleQuestions[4]
                  : ""
        );
        setSampleQuestion6(
            config.suggestedQuestions.length > 5
                ? config.suggestedQuestions[5].question
                : defaultConfig.sampleQuestions.length > 5
                  ? defaultConfig.sampleQuestions[5]
                  : ""
        );
    }, [config]);

    const isAvailableSamples = () => {
        return (
            !!title &&
            !!description &&
            !!sampleQuestion1 &&
            !!sampleQuestion2 &&
            !!sampleQuestion3 &&
            !!sampleQuestion4 &&
            !!sampleQuestion5 &&
            !!sampleQuestion6
        );
    };

    const handleQuestionClick = (text: string) => {
        handleQuestion(text);
    };

    return (
        <div
            className={`chat-background ${isAvailableSamples() ? "chat-background--visible" : "chat-background--hidden"}`}
        >
            <div className="chat-background-header">
                <div
                    className="chat-background-logo"
                    style={{ background: color, padding: logo === Logo.default ? 0 : 20 }}
                >
                    <img src={getLogoImg(logo, true, true)} alt="" />
                </div>
                <div className="chat-background-info">
                    <span className="chat-background-title">{title}</span>
                    <span className="chat-background-description">{description}</span>
                </div>
            </div>
            <div
                className={`chat-background-content ${theme === Theme.lightNanci || theme === Theme.darkNanci ? " chat-background-content--red" : ""}`}
            >
                <QuestionBubble
                    className="chat-background-item chat-background-item--medium"
                    icon={ZapIcon}
                    text={sampleQuestion1}
                    handleClick={handleQuestionClick}
                />
                <QuestionBubble
                    className="chat-background-item chat-background-item--medium"
                    icon={LayersIcon}
                    text={sampleQuestion2}
                    handleClick={handleQuestionClick}
                />
                <QuestionBubble
                    className="chat-background-item chat-background-item--large"
                    icon={BookIcon}
                    text={sampleQuestion3}
                    handleClick={handleQuestionClick}
                />
                <QuestionBubble
                    className="chat-background-item chat-background-item--small chat-background-item--mobile-hidden"
                    icon={GraphIcon}
                    text={sampleQuestion4}
                    handleClick={handleQuestionClick}
                />
                <QuestionBubble
                    className="chat-background-item chat-background-item--small"
                    icon={TelescopeIcon}
                    text={sampleQuestion5}
                    handleClick={handleQuestionClick}
                />
                <QuestionBubble
                    className="chat-background-item chat-background-item--small"
                    icon={GraduationHatIcon}
                    text={sampleQuestion6}
                    handleClick={handleQuestionClick}
                />
            </div>
        </div>
    );
};
